import { useCallback, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import type { DotLottie } from "@lottiefiles/dotlottie-web";

/**
 * Reduced-motion support for <DotLottieReact> players.
 *
 * Under `prefers-reduced-motion`, returns `autoplay: false` and a ref callback
 * that disables looping and freezes the animation on its **final frame**
 * (`setFrame` seeks *and* renders, so no extra render call is needed).
 *
 * ⚠️ Caveat: "last frame" is only a meaningful poster for animations with a
 * real end state (a checkmark drawing in, a badge settling). Looping/ambient
 * assets (confetti, sparkles, floating particles) often end on a blank or
 * arbitrary frame, so freezing there shows little or nothing. That's an
 * acceptable outcome for purely decorative motion, but it is not a curated
 * still — there is no generic "most representative frame" the player can pick.
 *
 * @example
 * const { autoplay, dotLottieRefCallback } = useLottieReducedMotion();
 * <DotLottieReact autoplay={autoplay} dotLottieRefCallback={dotLottieRefCallback} ... />
 */
export function useLottieReducedMotion() {
  const prefersReducedMotion = useReducedMotion();
  const instanceRef = useRef<DotLottie | null>(null);
  const onLoadRef = useRef<(() => void) | null>(null);

  const dotLottieRefCallback = useCallback(
    (dotLottie: DotLottie | null) => {
      // Detach any listener from a previous instance.
      if (instanceRef.current && onLoadRef.current) {
        instanceRef.current.removeEventListener("load", onLoadRef.current);
      }
      onLoadRef.current = null;
      instanceRef.current = dotLottie;

      if (!dotLottie || !prefersReducedMotion) return;

      const freezeOnLastFrame = () => {
        dotLottie.setLoop(false);
        dotLottie.setFrame(Math.max(0, dotLottie.totalFrames - 1));
      };

      // The asset may already be parsed by the time the ref attaches.
      if (dotLottie.isLoaded) {
        freezeOnLastFrame();
      } else {
        dotLottie.addEventListener("load", freezeOnLastFrame);
        onLoadRef.current = freezeOnLastFrame;
      }
    },
    [prefersReducedMotion],
  );

  return {
    autoplay: !prefersReducedMotion,
    prefersReducedMotion,
    dotLottieRefCallback,
  };
}
